import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

import { Typography } from '@telus-uds/components-web';
import Note from '../../note';
import formFieldOperationOptions from '../../utils/formFieldOperationOptions';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { isJson } from '../../utils/utils';
import { fieldSubscriberDetailsDevices } from '../../utils/constant';
import FieldGuide from './common/fieldGuide';
import { BoldFieldLabel } from './css';

const styles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderColor: '#54595f',
    fontSize: '13px',
    fontFamily: 'inherit',
    minHeight: '2.3rem'
  }),
  input: (provided) => ({
    ...provided,
    height: '2.3rem',
    fontSize: '13px',
    fontFamily: 'inherit',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    fontFamily: 'inherit',
    backgroundColor: state.isSelected
      ? '#2B8000'
      : state.isFocused
      ? '#b2d47e'
      : 'white',
    color: state.isSelected ? 'white' : 'black',
    cursor: 'pointer'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    fontFamily: 'inherit',
    fontSize: '13px'
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '200px',
    overflowY: 'auto',
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

const theme = options => {
  return {
    ...options,
    colors: {
      ...options.colors,
      primary25: '#b2d47e',
      primary: '#2B8000',
      primary50: '#b2d47e'
    }
  };
};

const FieldSelect = ({ data }) => {
  const {
    ref,
    name,
    label,
    value,
    required,
    options,
    onChange,
    note,
    tooltip,
    disabled,
    readonly,
    noteAppearsBelowField,
    isShowNoteBackground,
    isSearchable,
    placeholder
  } = data;
  const renderNotes = () => {
    return (
      note &&
      note.content && (
        <Note
          type="richText"
          options={formFieldOperationOptions(value)}
          data={note}
          isShowNoteBackground={isShowNoteBackground}
        />
      )
    );
  };

  const localizedStrings = useLocalizedStringsContext();
  const { quoteForm } = localizedStrings;

  const getSelectedDevice = (itemValue) => {
    const itemObject = isJson(itemValue) ? JSON.parse(itemValue) : itemValue;
    const valueObject = isJson(value) ? JSON.parse(value) : value;
    return itemObject.device_name && itemObject.device_name === valueObject.device_name && valueObject.device_name;
  };

  return (
    <div
      style={{
        marginTop: '15px',
        width: '100%',
        position: 'relative'
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        <BoldFieldLabel>
          <Typography block heading="h4" variant={{ size: 'h4', bold: true }}>
            {label}
            {required && `*`}
          </Typography>
        </BoldFieldLabel>
        {tooltip && (
          <span style={{ marginLeft: '0.5rem' }}>
            <FieldGuide tooltip={tooltip} />
          </span>
        )}
      </div>
      {!noteAppearsBelowField && renderNotes()}
      <div
        className="frg-select-container color-light"
        style={{ position: 'relative', width: '100%', height: '50px' }}
      >
        {isSearchable ? (
        <Select
          ref={ref}
          id={name}
          placeholder={placeholder}
          className="basic-single"
          classNamePrefix="select"
          styles={styles}
          theme={theme}
          isDisabled={disabled || readonly}
          isClearable={false}
          isSearchable
          name={name}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          onChange={e => onChange({ target: { name, value: e.value } })}
          value={
            options &&
            options
              .map(opt => {
                if (
                  opt &&
                  opt.fields &&
                  typeof opt.fields.label === 'string' &&
                  typeof opt.fields.value === 'string'
                ) {
                  return {
                    label: opt.fields.label,
                    value: opt.fields.value
                  };
                }
                return null;
              })
              .filter(Boolean)
              .find(opt => opt.value === value) || null
          }
          options={
            options &&
            options
              .map(opt => {
                if (
                  opt &&
                  opt.fields &&
                  typeof opt.fields.label === 'string' &&
                  typeof opt.fields.value === 'string'
                ) {
                  return {
                    label: opt.fields.label,
                    value: opt.fields.value
                  };
                }
                return null;
              })
              .filter(item => item !== null)
          }
          filterOption={(option, inputValue) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
          }
        />
      ) : (
        <select
          ref={ref}
          id={name}
          name={name}
          onChange={onChange}
          disabled={disabled || readonly}
        >
          <option key="option" value="" />
          {options &&
            options.map(item => {
              if (!item.fields) return null;

              const {
                fields: {
                  value: itemValue,
                  label: itemLabel,
                  price,
                  monthlyPayment,
                  showPricesInLabel
                }
              } = item;
              const key = `${name}option${itemValue}`;
              let optionLabel = itemLabel;
              if (showPricesInLabel) {
                if (price)
                  optionLabel = `${optionLabel}: ${quoteForm.upfront}: $${price}`;
                if (monthlyPayment)
                  optionLabel = `${optionLabel}${price ? ', ' : ': '} ${quoteForm.monthly
                    }: $${monthlyPayment}`;
              }

              return (
                <option
                  key={key}
                  value={itemValue}
                  selected={name === fieldSubscriberDetailsDevices ? getSelectedDevice(itemValue) : value === itemValue}
                >
                  {optionLabel}
                </option>
              );
            })}
        </select>
      )}
      </div>
      {noteAppearsBelowField && renderNotes()}
    </div>
  );
};

FieldSelect.propTypes = {
  data: PropTypes.shape({
    ref: PropTypes.object,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    required: PropTypes.bool,
    readonly: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          value: PropTypes.string.isRequired
        })
      })
    ),
    onChange: PropTypes.func.isRequired,
    note: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    disabled: PropTypes.bool,
    tooltip: PropTypes.string,
    noteAppearsBelowField: PropTypes.bool,
    isSearchable: PropTypes.bool
  }).isRequired
};

export default FieldSelect;
